import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Shield,
  Plus,
  Search,
  RefreshCw,
  Edit,
  Trash2,
  Copy,
  Play,
  Pause,
  Sparkles,
  Clock,
  FileCode,
  Globe,
  Network,
  Mail,
  User,
  Terminal,
  ChevronRight,
  ChevronDown,
  Save,
  X,
  Download,
  Upload,
  Zap,
  Check,
  AlertTriangle,
  MoreVertical,
  Settings2,
  TestTube,
  BarChart3,
  TrendingUp,
  Target,
  Ban,
  Activity,
  PieChart
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart as RechartsPie, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  dns: Globe,
  file: FileCode,
  process: Terminal,
  network: Network,
  email: Mail,
  user: User
};

const CATEGORY_COLORS = {
  dns: 'text-blue-400 bg-blue-500/10',
  file: 'text-purple-400 bg-purple-500/10',
  process: 'text-amber-400 bg-amber-500/10',
  network: 'text-green-400 bg-green-500/10',
  email: 'text-pink-400 bg-pink-500/10',
  user: 'text-cyan-400 bg-cyan-500/10'
};

export default function CustomRules() {
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState({});
  const [operators, setOperators] = useState({});
  const [actions, setActions] = useState({});
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [testingRule, setTestingRule] = useState(null);
  const [testData, setTestData] = useState('{}');
  const [testResult, setTestResult] = useState(null);
  const [aiPrompt, setAIPrompt] = useState('');
  const [aiLoading, setAILoading] = useState(false);
  const [generatedRule, setGeneratedRule] = useState(null);
  
  // Analytics state
  const [activeTab, setActiveTab] = useState('rules');
  const [analyticsOverview, setAnalyticsOverview] = useState(null);
  const [topRules, setTopRules] = useState([]);
  const [blockedItems, setBlockedItems] = useState([]);
  const [trends, setTrends] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState(7);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // New rule form state
  const [newRule, setNewRule] = useState({
    name: '',
    description: '',
    category: 'dns',
    priority: 50,
    enabled: true,
    condition_logic: 'all',
    conditions: [{ field: '', operator: 'equals', value: '', case_sensitive: false }],
    actions: [{ type: 'block', params: {} }],
    schedule: null,
    tags: [],
    notes: ''
  });

  useEffect(() => {
    fetchRules();
    fetchCategories();
    fetchTemplates();
  }, []);

  const fetchRules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRules(response.data.rules || []);
    } catch (error) {
      console.error('Failed to fetch rules:', error);
      // Demo data
      setRules(getDemoRules());
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/rules/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.categories || {});
      setOperators(response.data.operators || {});
      setActions(response.data.actions || {});
    } catch (error) {
      // Demo data
      setCategories({
        dns: { name: 'DNS Security', fields: ['domain', 'query_type', 'client_ip'] },
        file: { name: 'File Security', fields: ['file_name', 'file_hash', 'file_extension', 'file_path'] },
        process: { name: 'Process Control', fields: ['process_name', 'process_path', 'command_line'] },
        network: { name: 'Network Security', fields: ['source_ip', 'dest_ip', 'dest_port', 'protocol'] },
        email: { name: 'Email Security', fields: ['sender', 'recipient', 'subject', 'has_attachment'] },
        user: { name: 'User Behavior', fields: ['user_id', 'action_type', 'risk_score'] }
      });
      setOperators({
        equals: { label: 'Equals' },
        not_equals: { label: 'Not Equals' },
        contains: { label: 'Contains' },
        starts_with: { label: 'Starts With' },
        ends_with: { label: 'Ends With' },
        matches: { label: 'Matches Regex' },
        greater_than: { label: 'Greater Than' },
        less_than: { label: 'Less Than' },
        in_list: { label: 'In List' }
      });
      setActions({
        block: { label: 'Block' },
        allow: { label: 'Allow' },
        quarantine: { label: 'Quarantine' },
        notify: { label: 'Notify' },
        log: { label: 'Log Only' }
      });
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/rules/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      // Demo templates
      setTemplates([
        { id: '1', name: 'Block Executable Downloads', category: 'file', description: 'Block .exe/.msi files' },
        { id: '2', name: 'Block Social Media', category: 'dns', description: 'Block social media during work' },
        { id: '3', name: 'Quarantine Suspicious Attachments', category: 'email', description: 'Quarantine risky emails' }
      ]);
    }
  };

  const getDemoRules = () => [
    {
      id: '1',
      name: 'Block Malicious Domains',
      description: 'Block access to known malicious domains',
      category: 'dns',
      priority: 90,
      enabled: true,
      condition_logic: 'any',
      conditions: [{ field: 'domain', operator: 'contains', value: 'malware' }],
      actions: [{ type: 'block', params: {} }],
      schedule: null,
      hit_count: 245,
      created_at: new Date().toISOString()
    },
    {
      id: '2',
      name: 'Quarantine EXE Downloads',
      description: 'Quarantine executable file downloads',
      category: 'file',
      priority: 85,
      enabled: true,
      condition_logic: 'all',
      conditions: [{ field: 'file_extension', operator: 'in_list', value: 'exe,msi,bat' }],
      actions: [{ type: 'quarantine', params: {} }],
      schedule: null,
      hit_count: 89,
      created_at: new Date().toISOString()
    },
    {
      id: '3',
      name: 'Block Social Media (Work Hours)',
      description: 'Block social media during business hours',
      category: 'dns',
      priority: 60,
      enabled: true,
      condition_logic: 'any',
      conditions: [{ field: 'domain', operator: 'matches', value: '.*facebook\\.com$' }],
      actions: [{ type: 'block', params: {} }],
      schedule: { enabled: true, days_of_week: [0,1,2,3,4], start_time: '09:00', end_time: '17:00' },
      hit_count: 567,
      created_at: new Date().toISOString()
    }
  ];

  // Analytics fetch functions
  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const [overviewRes, topRulesRes, blockedRes, trendsRes] = await Promise.all([
        axios.get(`${API}/api/rules/analytics/overview?days=${analyticsPeriod}`, { headers }),
        axios.get(`${API}/api/rules/analytics/top-rules?days=${analyticsPeriod}&limit=10`, { headers }),
        axios.get(`${API}/api/rules/analytics/blocked-items?days=${analyticsPeriod}&limit=15`, { headers }),
        axios.get(`${API}/api/rules/analytics/trends?days=${analyticsPeriod}`, { headers })
      ]);
      
      setAnalyticsOverview(overviewRes.data);
      setTopRules(topRulesRes.data.top_rules || []);
      setBlockedItems(blockedRes.data);
      setTrends(trendsRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      // Set demo analytics data
      setAnalyticsOverview(getDemoAnalytics());
      setTopRules(getDemoTopRules());
      setBlockedItems(getDemoBlockedItems());
      setTrends(getDemoTrends());
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const generateDemoData = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/rules/analytics/generate-demo-data?days=7`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Demo analytics data generated!');
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to generate demo data');
    }
  };

  const getDemoAnalytics = () => ({
    period_days: 7,
    total_rules: 5,
    active_rules: 4,
    total_triggers: 1247,
    true_positives: 1185,
    false_positives: 62,
    effectiveness_rate: 95.0,
    triggers_by_action: { block: 890, quarantine: 234, notify: 98, log: 25 },
    daily_trend: [
      { date: '2025-01-13', count: 156 },
      { date: '2025-01-14', count: 189 },
      { date: '2025-01-15', count: 167 },
      { date: '2025-01-16', count: 201 },
      { date: '2025-01-17', count: 178 },
      { date: '2025-01-18', count: 192 },
      { date: '2025-01-19', count: 164 }
    ]
  });

  const getDemoTopRules = () => [
    { id: '1', name: 'Block Malicious Domains', category: 'dns', total_hits: 456, false_positives: 12, effectiveness_rate: 97.4 },
    { id: '2', name: 'Quarantine Suspicious Files', category: 'file', total_hits: 234, false_positives: 18, effectiveness_rate: 92.3 },
    { id: '3', name: 'Block Known Bad IPs', category: 'network', total_hits: 189, false_positives: 5, effectiveness_rate: 97.4 },
    { id: '4', name: 'Alert High Risk Users', category: 'user', total_hits: 98, false_positives: 15, effectiveness_rate: 84.7 },
    { id: '5', name: 'Log Email Attachments', category: 'email', total_hits: 67, false_positives: 2, effectiveness_rate: 97.0 }
  ];

  const getDemoBlockedItems = () => ({
    blocked_items: [
      { category: 'dns', value: 'malware-site.com', block_count: 145, triggered_by_rules: 2 },
      { category: 'dns', value: 'phishing-bank.net', block_count: 98, triggered_by_rules: 1 },
      { category: 'file', value: 'trojan.exe', block_count: 67, triggered_by_rules: 1 },
      { category: 'network', value: '192.168.100.1', block_count: 54, triggered_by_rules: 2 },
      { category: 'dns', value: 'crypto-scam.io', block_count: 43, triggered_by_rules: 1 }
    ],
    category_breakdown: { dns: 456, file: 234, network: 189, email: 67, user: 45 }
  });

  const getDemoTrends = () => ({
    category_trend: [
      { date: '2025-01-13', dns: 78, file: 45, network: 33 },
      { date: '2025-01-14', dns: 89, file: 52, network: 48 },
      { date: '2025-01-15', dns: 67, file: 48, network: 52 },
      { date: '2025-01-16', dns: 95, file: 56, network: 50 },
      { date: '2025-01-17', dns: 82, file: 51, network: 45 },
      { date: '2025-01-18', dns: 91, file: 54, network: 47 },
      { date: '2025-01-19', dns: 74, file: 49, network: 41 }
    ],
    action_trend: [
      { date: '2025-01-13', block: 120, quarantine: 25, notify: 11 },
      { date: '2025-01-14', block: 145, quarantine: 32, notify: 12 },
      { date: '2025-01-15', block: 128, quarantine: 28, notify: 11 },
      { date: '2025-01-16', block: 156, quarantine: 35, notify: 10 },
      { date: '2025-01-17', block: 134, quarantine: 30, notify: 14 },
      { date: '2025-01-18', block: 148, quarantine: 33, notify: 11 },
      { date: '2025-01-19', block: 127, quarantine: 27, notify: 10 }
    ]
  });

  useEffect(() => {
    if (activeTab === 'analytics') {
      fetchAnalytics();
    }
  }, [activeTab, analyticsPeriod]);

  const handleCreateRule = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/rules`, newRule, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rule created successfully');
      setShowCreateDialog(false);
      fetchRules();
      resetNewRule();
    } catch (error) {
      toast.error('Failed to create rule');
    }
  };

  const handleUpdateRule = async () => {
    if (!editingRule) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/rules/${editingRule.id}`, newRule, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rule updated successfully');
      setEditingRule(null);
      setShowCreateDialog(false);
      fetchRules();
      resetNewRule();
    } catch (error) {
      toast.error('Failed to update rule');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!confirm('Are you sure you want to delete this rule?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/rules/${ruleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rule deleted');
      fetchRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const handleToggleRule = async (ruleId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/rules/${ruleId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRules();
    } catch (error) {
      toast.error('Failed to toggle rule');
    }
  };

  const handleTestRule = async () => {
    if (!testingRule) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/rules/test`, {
        rule_id: testingRule.id,
        test_data: JSON.parse(testData)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestResult(response.data);
    } catch (error) {
      toast.error('Test failed: Invalid JSON or server error');
    }
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please enter a description');
      return;
    }
    setAILoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/rules/ai/generate`, {
        prompt: aiPrompt
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setGeneratedRule(response.data.generated_rule);
        toast.success('Rule generated! Review and save.');
      } else {
        toast.error(response.data.error || 'Generation failed');
      }
    } catch (error) {
      toast.error('AI generation failed');
    } finally {
      setAILoading(false);
    }
  };

  const applyGeneratedRule = () => {
    if (generatedRule) {
      setNewRule({
        ...newRule,
        ...generatedRule,
        conditions: generatedRule.conditions || [],
        actions: generatedRule.actions || []
      });
      setShowAIDialog(false);
      setShowCreateDialog(true);
    }
  };

  const resetNewRule = () => {
    setNewRule({
      name: '',
      description: '',
      category: 'dns',
      priority: 50,
      enabled: true,
      condition_logic: 'all',
      conditions: [{ field: '', operator: 'equals', value: '', case_sensitive: false }],
      actions: [{ type: 'block', params: {} }],
      schedule: null,
      tags: [],
      notes: ''
    });
    setEditingRule(null);
  };

  const openEditDialog = (rule) => {
    setEditingRule(rule);
    setNewRule({
      ...rule,
      conditions: rule.conditions || [],
      actions: rule.actions || []
    });
    setShowCreateDialog(true);
  };

  const addCondition = () => {
    setNewRule({
      ...newRule,
      conditions: [...newRule.conditions, { field: '', operator: 'equals', value: '', case_sensitive: false }]
    });
  };

  const removeCondition = (index) => {
    setNewRule({
      ...newRule,
      conditions: newRule.conditions.filter((_, i) => i !== index)
    });
  };

  const updateCondition = (index, field, value) => {
    const updated = [...newRule.conditions];
    updated[index] = { ...updated[index], [field]: value };
    setNewRule({ ...newRule, conditions: updated });
  };

  const filteredRules = rules.filter(rule => {
    const matchesSearch = rule.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rule.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || rule.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Chart colors
  const CHART_COLORS = ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#ec4899', '#06b6d4'];
  const ACTION_COLORS = { block: '#ef4444', quarantine: '#f59e0b', notify: '#3b82f6', log: '#6b7280', allow: '#10b981' };

  // Analytics content rendered inline to avoid unstable nested component
  const renderAnalyticsContent = () => (
    <div className="space-y-6">
      {/* Analytics Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Select value={analyticsPeriod.toString()} onValueChange={(v) => setAnalyticsPeriod(parseInt(v))}>
            <SelectTrigger className="w-32 bg-slate-800 border-slate-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAnalytics} disabled={analyticsLoading} className="gap-2">
            <RefreshCw className={`w-4 h-4 ${analyticsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        <Button variant="outline" size="sm" onClick={generateDemoData} className="gap-2">
          <Zap className="w-4 h-4" />
          Generate Demo Data
        </Button>
      </div>

      {analyticsLoading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
        </div>
      ) : (
        <>
          {/* Overview Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <Activity className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">Total Triggers</p>
                    <p className="text-xl font-bold text-white">{analyticsOverview?.total_triggers?.toLocaleString() || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/10">
                    <Target className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">True Positives</p>
                    <p className="text-xl font-bold text-green-400">{analyticsOverview?.true_positives?.toLocaleString() || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-500/10">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">False Positives</p>
                    <p className="text-xl font-bold text-red-400">{analyticsOverview?.false_positives || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/10">
                    <TrendingUp className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">Effectiveness</p>
                    <p className="text-xl font-bold text-purple-400">{analyticsOverview?.effectiveness_rate || 0}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/10">
                    <Shield className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">Active Rules</p>
                    <p className="text-xl font-bold text-amber-400">{analyticsOverview?.active_rules || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-cyan-500/10">
                    <Ban className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">Blocks</p>
                    <p className="text-xl font-bold text-cyan-400">{analyticsOverview?.triggers_by_action?.block?.toLocaleString() || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Triggers Trend */}
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-base">Rule Triggers Over Time</CardTitle>
                <CardDescription>Daily rule trigger volume</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={analyticsOverview?.daily_trend || []}>
                      <defs>
                        <linearGradient id="colorTriggers" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="date" stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 12 }} tickFormatter={(v) => v?.slice(5)} />
                      <YAxis stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                      <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }} labelStyle={{ color: '#f1f5f9' }} />
                      <Area type="monotone" dataKey="count" stroke="#3b82f6" fill="url(#colorTriggers)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Actions Breakdown */}
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-base">Actions Breakdown</CardTitle>
                <CardDescription>Distribution of rule actions taken</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={Object.entries(analyticsOverview?.triggers_by_action || {}).map(([name, value]) => ({ name, value }))}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {Object.entries(analyticsOverview?.triggers_by_action || {}).map(([name], index) => (
                          <Cell key={`cell-${index}`} fill={ACTION_COLORS[name] || CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }} />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Category Trend */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base">Triggers by Category</CardTitle>
              <CardDescription>Category distribution over time</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trends?.category_trend || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="date" stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 12 }} tickFormatter={(v) => v?.slice(5)} />
                    <YAxis stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }} labelStyle={{ color: '#f1f5f9' }} />
                    <Legend />
                    <Bar dataKey="dns" fill="#3b82f6" name="DNS" />
                    <Bar dataKey="file" fill="#8b5cf6" name="File" />
                    <Bar dataKey="network" fill="#10b981" name="Network" />
                    <Bar dataKey="email" fill="#ec4899" name="Email" />
                    <Bar dataKey="user" fill="#f59e0b" name="User" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Top Rules and Blocked Items */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Triggered Rules */}
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                  Top Triggered Rules
                </CardTitle>
                <CardDescription>Most active rules by trigger count</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {topRules.length === 0 ? (
                    <p className="text-slate-400 text-sm text-center py-4">No rule triggers recorded yet</p>
                  ) : (
                    topRules.slice(0, 5).map((rule, index) => {
                      const CategoryIcon = CATEGORY_ICONS[rule.category] || Shield;
                      return (
                        <div key={rule.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="text-slate-500 font-mono text-sm w-5">#{index + 1}</span>
                            <div className={`p-1.5 rounded ${CATEGORY_COLORS[rule.category] || 'bg-slate-700'}`}>
                              <CategoryIcon className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-white">{rule.name}</p>
                              <p className="text-xs text-slate-400">{rule.total_hits} triggers</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-sm font-medium ${rule.effectiveness_rate >= 90 ? 'text-green-400' : rule.effectiveness_rate >= 70 ? 'text-amber-400' : 'text-red-400'}`}>
                              {rule.effectiveness_rate}%
                            </p>
                            <p className="text-xs text-slate-500">effectiveness</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Most Blocked Items */}
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Ban className="w-5 h-5 text-red-400" />
                  Most Blocked Items
                </CardTitle>
                <CardDescription>Top blocked domains, files, and IPs</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(blockedItems?.blocked_items || []).length === 0 ? (
                    <p className="text-slate-400 text-sm text-center py-4">No blocked items recorded yet</p>
                  ) : (
                    (blockedItems?.blocked_items || []).slice(0, 5).map((item, index) => {
                      const CategoryIcon = CATEGORY_ICONS[item.category] || Shield;
                      return (
                        <div key={index} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`p-1.5 rounded ${CATEGORY_COLORS[item.category] || 'bg-slate-700'}`}>
                              <CategoryIcon className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-white font-mono">{item.value}</p>
                              <p className="text-xs text-slate-400">{item.category}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-red-400">{item.block_count}</p>
                            <p className="text-xs text-slate-500">blocks</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Category Breakdown */}
          {blockedItems?.category_breakdown && Object.keys(blockedItems.category_breakdown).length > 0 && (
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-base">Blocks by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {Object.entries(blockedItems.category_breakdown).map(([category, count]) => {
                    const CategoryIcon = CATEGORY_ICONS[category] || Shield;
                    return (
                      <div key={category} className={`p-4 rounded-lg ${CATEGORY_COLORS[category] || 'bg-slate-800'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <CategoryIcon className="w-5 h-5" />
                          <span className="text-sm font-medium capitalize">{category}</span>
                        </div>
                        <p className="text-2xl font-bold">{count.toLocaleString()}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="custom-rules-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Custom Rules Engine</h1>
          <p className="text-slate-400 text-sm mt-1">Create and manage security rules with AI assistance</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowAIDialog(true)} className="gap-2">
            <Sparkles className="w-4 h-4" />
            AI Generate
          </Button>
          <Button onClick={() => { resetNewRule(); setShowCreateDialog(true); }} className="gap-2 bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4" />
            New Rule
          </Button>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-800 border-slate-700">
          <TabsTrigger value="rules" className="gap-2 data-[state=active]:bg-slate-700">
            <Shield className="w-4 h-4" />
            Rules
          </TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2 data-[state=active]:bg-slate-700">
            <BarChart3 className="w-4 h-4" />
            Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rules" className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs uppercase">Total Rules</p>
                <p className="text-2xl font-bold text-white mt-1">{rules.length}</p>
              </div>
              <Shield className="w-8 h-8 text-blue-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs uppercase">Active</p>
                <p className="text-2xl font-bold text-green-400 mt-1">{rules.filter(r => r.enabled).length}</p>
              </div>
              <Check className="w-8 h-8 text-green-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs uppercase">Scheduled</p>
                <p className="text-2xl font-bold text-amber-400 mt-1">{rules.filter(r => r.schedule?.enabled).length}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs uppercase">Total Hits (7d)</p>
                <p className="text-2xl font-bold text-purple-400 mt-1">{rules.reduce((sum, r) => sum + (r.hit_count || 0), 0)}</p>
              </div>
              <Zap className="w-8 h-8 text-purple-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Search rules..."
            className="pl-10 bg-slate-800 border-slate-700"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-40 bg-slate-800 border-slate-700">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="dns">DNS Security</SelectItem>
            <SelectItem value="file">File Security</SelectItem>
            <SelectItem value="process">Process Control</SelectItem>
            <SelectItem value="network">Network Security</SelectItem>
            <SelectItem value="email">Email Security</SelectItem>
            <SelectItem value="user">User Behavior</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Rules List */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="w-6 h-6 text-blue-400 animate-spin" />
            </div>
          ) : filteredRules.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No rules found</p>
              <Button onClick={() => setShowCreateDialog(true)} className="mt-4 gap-2">
                <Plus className="w-4 h-4" />
                Create First Rule
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {filteredRules.map((rule) => {
                const CategoryIcon = CATEGORY_ICONS[rule.category] || Shield;
                return (
                  <div
                    key={rule.id}
                    className="flex items-center justify-between p-4 hover:bg-slate-800/50 transition-colors"
                    data-testid={`rule-${rule.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${CATEGORY_COLORS[rule.category] || 'bg-slate-700'}`}>
                        <CategoryIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-white">{rule.name}</h3>
                          <Badge variant="outline" className="text-xs">
                            P{rule.priority}
                          </Badge>
                          {rule.schedule?.enabled && (
                            <Badge className="text-xs bg-amber-500/20 text-amber-400 gap-1">
                              <Clock className="w-3 h-3" />
                              Scheduled
                            </Badge>
                          )}
                          {!rule.enabled && (
                            <Badge variant="secondary" className="text-xs">Disabled</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-400 mt-0.5">{rule.description}</p>
                        <div className="flex gap-4 mt-1 text-xs text-slate-500">
                          <span>{rule.conditions?.length || 0} conditions</span>
                          <span>{rule.actions?.length || 0} actions</span>
                          <span>{rule.hit_count || 0} hits (7d)</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={rule.enabled}
                        onCheckedChange={() => handleToggleRule(rule.id)}
                      />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                          <DropdownMenuItem onClick={() => openEditDialog(rule)} className="gap-2">
                            <Edit className="w-4 h-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setTestingRule(rule); setShowTestDialog(true); }} className="gap-2">
                            <TestTube className="w-4 h-4" />
                            Test
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigator.clipboard.writeText(JSON.stringify(rule, null, 2))} className="gap-2">
                            <Copy className="w-4 h-4" />
                            Copy JSON
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDeleteRule(rule.id)} className="gap-2 text-red-400">
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Templates Section */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white text-base">Rule Templates</CardTitle>
          <CardDescription>Quick-start with pre-built security rules</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {templates.map((template) => {
              const Icon = CATEGORY_ICONS[template.category] || Shield;
              return (
                <div
                  key={template.id}
                  className="p-3 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-slate-600 cursor-pointer transition-colors"
                  onClick={() => {
                    setNewRule({ ...newRule, ...template, name: template.name });
                    setShowCreateDialog(true);
                  }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={`w-4 h-4 ${CATEGORY_COLORS[template.category]?.split(' ')[0] || 'text-slate-400'}`} />
                    <span className="text-sm font-medium text-white">{template.name}</span>
                  </div>
                  <p className="text-xs text-slate-400">{template.description}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="analytics">
          {renderAnalyticsContent()}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Rule Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingRule ? 'Edit Rule' : 'Create Custom Rule'}
            </DialogTitle>
            <DialogDescription>
              Define conditions and actions for your security rule
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-sm text-slate-400 mb-1 block">Rule Name</label>
                <Input
                  value={newRule.name}
                  onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                  placeholder="e.g., Block Malicious Downloads"
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Category</label>
                <Select value={newRule.category} onValueChange={(v) => setNewRule({ ...newRule, category: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dns">DNS Security</SelectItem>
                    <SelectItem value="file">File Security</SelectItem>
                    <SelectItem value="process">Process Control</SelectItem>
                    <SelectItem value="network">Network Security</SelectItem>
                    <SelectItem value="email">Email Security</SelectItem>
                    <SelectItem value="user">User Behavior</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Priority (1-100)</label>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={newRule.priority}
                  onChange={(e) => setNewRule({ ...newRule, priority: parseInt(e.target.value) || 50 })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm text-slate-400 mb-1 block">Description</label>
                <Textarea
                  value={newRule.description}
                  onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
                  placeholder="Describe what this rule does..."
                  className="bg-slate-800 border-slate-700"
                  rows={2}
                />
              </div>
            </div>

            {/* Conditions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-slate-400">Conditions</label>
                <Select value={newRule.condition_logic} onValueChange={(v) => setNewRule({ ...newRule, condition_logic: v })}>
                  <SelectTrigger className="w-32 h-8 bg-slate-800 border-slate-700 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Match ALL</SelectItem>
                    <SelectItem value="any">Match ANY</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                {newRule.conditions.map((condition, index) => (
                  <div key={index} className="flex gap-2 items-center p-2 bg-slate-800/50 rounded-lg">
                    <Select
                      value={condition.field}
                      onValueChange={(v) => updateCondition(index, 'field', v)}
                    >
                      <SelectTrigger className="w-32 bg-slate-700 border-slate-600 text-xs">
                        <SelectValue placeholder="Field" />
                      </SelectTrigger>
                      <SelectContent>
                        {(categories[newRule.category]?.fields || []).map(f => (
                          <SelectItem key={f} value={f}>{f}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={condition.operator}
                      onValueChange={(v) => updateCondition(index, 'operator', v)}
                    >
                      <SelectTrigger className="w-32 bg-slate-700 border-slate-600 text-xs">
                        <SelectValue placeholder="Operator" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(operators).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={condition.value}
                      onChange={(e) => updateCondition(index, 'value', e.target.value)}
                      placeholder="Value"
                      className="flex-1 bg-slate-700 border-slate-600 text-sm"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-400"
                      onClick={() => removeCondition(index)}
                      disabled={newRule.conditions.length === 1}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addCondition} className="gap-1">
                  <Plus className="w-3 h-3" />
                  Add Condition
                </Button>
              </div>
            </div>

            {/* Actions */}
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Actions</label>
              <Select
                value={newRule.actions[0]?.type || 'block'}
                onValueChange={(v) => setNewRule({ ...newRule, actions: [{ type: v, params: {} }] })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(actions).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Schedule */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-slate-400">Schedule (Optional)</label>
                <Switch
                  checked={newRule.schedule?.enabled || false}
                  onCheckedChange={(checked) => setNewRule({
                    ...newRule,
                    schedule: checked ? {
                      enabled: true,
                      days_of_week: [0, 1, 2, 3, 4],
                      start_time: '09:00',
                      end_time: '17:00',
                      timezone: 'UTC',
                      invert: false
                    } : null
                  })}
                />
              </div>
              {newRule.schedule?.enabled && (
                <div className="p-3 bg-slate-800/50 rounded-lg space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500">Start Time</label>
                      <Input
                        type="time"
                        value={newRule.schedule.start_time}
                        onChange={(e) => setNewRule({
                          ...newRule,
                          schedule: { ...newRule.schedule, start_time: e.target.value }
                        })}
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">End Time</label>
                      <Input
                        type="time"
                        value={newRule.schedule.end_time}
                        onChange={(e) => setNewRule({
                          ...newRule,
                          schedule: { ...newRule.schedule, end_time: e.target.value }
                        })}
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newRule.schedule.invert}
                      onChange={(e) => setNewRule({
                        ...newRule,
                        schedule: { ...newRule.schedule, invert: e.target.checked }
                      })}
                      className="rounded"
                    />
                    <span className="text-xs text-slate-400">Invert (rule is INACTIVE during this time)</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetNewRule(); }}>
              Cancel
            </Button>
            <Button onClick={editingRule ? handleUpdateRule : handleCreateRule} className="gap-2">
              <Save className="w-4 h-4" />
              {editingRule ? 'Update Rule' : 'Create Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Rule Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Test Rule</DialogTitle>
            <DialogDescription>
              Test &ldquo;{testingRule?.name}&rdquo; against sample data
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Test Data (JSON)</label>
              <Textarea
                value={testData}
                onChange={(e) => setTestData(e.target.value)}
                placeholder='{"domain": "example.com", "risk_score": 75}'
                className="bg-slate-800 border-slate-700 font-mono text-sm"
                rows={5}
              />
            </div>
            
            {testResult && (
              <div className={`p-4 rounded-lg ${testResult.would_trigger ? 'bg-red-500/10 border border-red-500/30' : 'bg-green-500/10 border border-green-500/30'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {testResult.would_trigger ? (
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                  ) : (
                    <Check className="w-5 h-5 text-green-400" />
                  )}
                  <span className={`font-medium ${testResult.would_trigger ? 'text-red-400' : 'text-green-400'}`}>
                    {testResult.would_trigger ? 'Rule Would Trigger' : 'Rule Would Not Trigger'}
                  </span>
                </div>
                <div className="text-xs text-slate-400 space-y-1">
                  <p>Conditions matched: {testResult.matches_conditions ? 'Yes' : 'No'}</p>
                  <p>Schedule active: {testResult.schedule_active ? 'Yes' : 'No'}</p>
                  <p>Rule enabled: {testResult.rule_enabled ? 'Yes' : 'No'}</p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowTestDialog(false); setTestResult(null); }}>
              Close
            </Button>
            <Button onClick={handleTestRule} className="gap-2">
              <Play className="w-4 h-4" />
              Run Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Generate Dialog */}
      <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              AI Rule Generator
            </DialogTitle>
            <DialogDescription>
              Describe what you want to achieve and AI will generate a rule
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Textarea
              value={aiPrompt}
              onChange={(e) => setAIPrompt(e.target.value)}
              placeholder="Example: Block all downloads of executable files from unknown sources and alert the admin"
              className="bg-slate-800 border-slate-700"
              rows={4}
            />
            
            {generatedRule && (
              <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                <h4 className="text-sm font-medium text-white mb-2">Generated Rule Preview</h4>
                <div className="space-y-1 text-sm">
                  <p><span className="text-slate-400">Name:</span> <span className="text-white">{generatedRule.name}</span></p>
                  <p><span className="text-slate-400">Category:</span> <span className="text-white">{generatedRule.category}</span></p>
                  <p><span className="text-slate-400">Conditions:</span> <span className="text-white">{generatedRule.conditions?.length || 0}</span></p>
                  <p><span className="text-slate-400">Actions:</span> <span className="text-white">{generatedRule.actions?.map(a => a.type).join(', ')}</span></p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAIDialog(false); setGeneratedRule(null); setAIPrompt(''); }}>
              Cancel
            </Button>
            {generatedRule ? (
              <Button onClick={applyGeneratedRule} className="gap-2">
                <Check className="w-4 h-4" />
                Use This Rule
              </Button>
            ) : (
              <Button onClick={handleAIGenerate} disabled={aiLoading} className="gap-2">
                {aiLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                Generate
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
